import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';
import {
  languageLabels,
  supportedLanguages,
  SupportedLanguage,
  translations,
  translateUiPhrase,
} from './translations';

type LanguageContextValue = {
  language: SupportedLanguage;
  orgDefaultLanguage: SupportedLanguage;
  effectiveLanguage: SupportedLanguage;
  ready: boolean;
  t: (key: string, fallback?: string) => string;
  tr: (phrase: string) => string;
  setLanguage: (language: SupportedLanguage) => void;
  refresh: () => Promise<void>;
};

const hostDefaultLanguageMap: Record<string, SupportedLanguage> = {
  'app.elen1.com': 'es',
  'mi.elen1.com': 'es',
};

const LANGUAGE_STORAGE_KEY = 'elen1.language';

function readStoredLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

function persistLanguage(language: SupportedLanguage) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // storage unavailable (private mode, blocked) — selection still applies for the session
  }
}

function normalizeLanguage(value?: unknown): SupportedLanguage | null {
  if (typeof value !== 'string') return null;
  const lowered = value.trim().toLowerCase();
  if (lowered.startsWith('en')) return 'en';
  if (lowered.startsWith('es')) return 'es';
  if (lowered.startsWith('it')) return 'it';
  return null;
}

function resolveDomainDefaultLanguage() {
  if (typeof window === 'undefined') return 'es' as SupportedLanguage;
  return hostDefaultLanguageMap[window.location.hostname] ?? 'es';
}

function resolveDomainLanguageFromOrgMap(domainMap: Record<string, unknown> | null | undefined) {
  if (typeof window === 'undefined') return null;
  if (!domainMap || typeof domainMap !== 'object') return null;
  const hostname = window.location.hostname;
  const candidate = normalizeLanguage((domainMap as Record<string, unknown>)[hostname]);
  return candidate;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const domainDefault = resolveDomainDefaultLanguage();
  const initialLanguage = readStoredLanguage() ?? domainDefault;
  const [language, setLanguageState] = useState<SupportedLanguage>(initialLanguage);
  const [orgDefaultLanguage, setOrgDefaultLanguage] = useState<SupportedLanguage>(domainDefault);
  const [effectiveLanguage, setEffectiveLanguage] = useState<SupportedLanguage>(initialLanguage);
  const [ready, setReady] = useState(false);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) {
      await refreshInFlightRef.current;
      return;
    }

    const request = (async () => {
      if (!isAuthenticated) {
        const visitorLanguage = readStoredLanguage() ?? domainDefault;
        setOrgDefaultLanguage(domainDefault);
        setLanguageState(visitorLanguage);
        setEffectiveLanguage(visitorLanguage);
        setReady(true);
        return;
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH_AUDIENCE },
        });
        const response = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        const payload = await response.json();

        const orgDomainDefault =
          resolveDomainLanguageFromOrgMap(payload?.organization?.domainLanguageDefaults as Record<string, unknown> | undefined) ??
          domainDefault;
        const orgLang = normalizeLanguage(payload?.organization?.defaultLanguage) ?? orgDomainDefault;
        const preferred = normalizeLanguage(payload?.preferredLanguage);
        const resolved = preferred ?? orgLang ?? orgDomainDefault ?? domainDefault;

        setOrgDefaultLanguage(orgLang);
        setLanguageState(resolved);
        setEffectiveLanguage(resolved);
      } catch {
        const fallbackLanguage = readStoredLanguage() ?? domainDefault;
        setOrgDefaultLanguage(domainDefault);
        setLanguageState(fallbackLanguage);
        setEffectiveLanguage(fallbackLanguage);
      } finally {
        setReady(true);
      }
    })();

    refreshInFlightRef.current = request;
    try {
      await request;
    } finally {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
    }
  }, [domainDefault, getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (isLoading) return;
    refresh();
  }, [isLoading, isAuthenticated, refresh]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      orgDefaultLanguage,
      effectiveLanguage,
      ready,
      t: (key, fallback) => {
        const table = translations[language] ?? translations.en;
        const translated = (table as Record<string, string>)[key];
        if (translated) return translated;
        if (fallback) return translateUiPhrase(language, fallback);
        return translateUiPhrase(language, key);
      },
      tr: (phrase) => translateUiPhrase(language, phrase),
      setLanguage: (nextLanguage) => {
        persistLanguage(nextLanguage);
        setLanguageState(nextLanguage);
        setEffectiveLanguage(nextLanguage);
      },
      refresh,
    }),
    [effectiveLanguage, language, orgDefaultLanguage, ready, refresh],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useI18n must be used inside LanguageProvider');
  }
  return context;
}

export { languageLabels, supportedLanguages };
